import React, { useEffect, useState } from 'react';
import { Check, Copy, KeyRound, Plus, Power, Trash2 } from 'lucide-react';
import { ApiUser, createApiUser, deleteApiUser, listApiUsers, updateApiUser } from '../api/services';
import './ApiUsers.css';

export default function ApiUsers() {
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [secret, setSecret] = useState('');
  const [form, setForm] = useState({ clientVersion: '1.0.0', platform: 'darwin', architecture: '', locale: 'en-GB' });
  const [message, setMessage] = useState('');

  const loadUsers = async () => {
    setLoading(true);
    try { setUsers((await listApiUsers()).users); } finally { setLoading(false); }
  };
  useEffect(() => { void loadUsers(); }, []);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    const result = await createApiUser(form);
    setSecret(result.clientSecret);
    setShowCreate(false);
    setMessage('API user created. Copy the secret now; it cannot be viewed again.');
    await loadUsers();
  };

  const toggle = async (user: ApiUser) => {
    await updateApiUser(user.id, user.status === 'active' ? 'disabled' : 'active');
    await loadUsers();
  };

  const remove = async (user: ApiUser) => {
    if (!window.confirm(`Delete API access for ${user.clientId}?`)) return;
    await deleteApiUser(user.id);
    await loadUsers();
  };

  const copySecret = async () => { await navigator.clipboard.writeText(secret); setMessage('Client secret copied.'); };

  return (
    <div className="api-users">
      <div className="page-header api-users-header">
        <div><h2>API Users</h2><p>Manage client credentials and access to telemetry ingestion.</p></div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={17} /> Create API user</button>
      </div>

      {message && <div className="api-users-notice">{message}</div>}
      {secret && <div className="secret-panel"><div><strong>New client secret</strong><span>Save this secret in the client keychain. It will not be shown again.</span></div><code>{secret}</code><button className="btn btn-secondary" onClick={copySecret}><Copy size={15} /> Copy secret</button></div>}

      <div className="api-users-summary"><div><KeyRound size={18} /><span><strong>{users.length}</strong> API users</span></div><span>Credentials are scoped to telemetry ingestion</span></div>
      <div className="api-users-table-wrap">
        {loading ? <div className="loading">Loading API users...</div> : <table className="api-users-table"><thead><tr><th>Client ID</th><th>Platform</th><th>Version</th><th>Last seen</th><th>Status</th><th>Actions</th></tr></thead><tbody>{users.map((user) => <tr key={user.id}><td><code>{user.clientId}</code></td><td>{user.platform}</td><td>{user.clientVersion}</td><td>{user.lastSeenAt ? new Date(user.lastSeenAt).toLocaleString() : 'Never'}</td><td><span className={`user-status ${user.status}`}><span />{user.status}</span></td><td><div className="user-actions"><button title={user.status === 'active' ? 'Disable API access' : 'Enable API access'} onClick={() => void toggle(user)}><Power size={15} /></button><button title="Delete API user" onClick={() => void remove(user)}><Trash2 size={15} /></button></div></td></tr>)}{users.length === 0 && <tr><td colSpan={6} className="empty-users">No API users yet.</td></tr>}</tbody></table>}
      </div>

      {showCreate && <div className="modal-backdrop" onClick={() => setShowCreate(false)}><div className="api-user-modal" onClick={(event) => event.stopPropagation()}><div className="modal-heading"><div><h3>Create API user</h3><p>Generate credentials for an assistive client.</p></div><button onClick={() => setShowCreate(false)}>×</button></div><form onSubmit={create}><label>Client version<input value={form.clientVersion} onChange={(e) => setForm({ ...form, clientVersion: e.target.value })} required /></label><label>Platform<select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}><option value="darwin">macOS</option><option value="win32">Windows</option><option value="linux">Linux</option></select></label><label>Architecture<input value={form.architecture} onChange={(e) => setForm({ ...form, architecture: e.target.value })} placeholder="arm64" /></label><label>Locale<input value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })} /></label><div className="modal-actions"><button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button><button type="submit" className="btn btn-primary"><Check size={15} /> Create credentials</button></div></form></div></div>}
    </div>
  );
}
